import { createComponentStore } from '../../utils/createComponentStore';

export const MODE_WEBHOOK = 'webhook';
export const MODE_POLLING = 'polling';
export const WEBHOOK_MODE_STATIC = 'static';
export const WEBHOOK_MODE_DYNAMIC = 'dynamic';
export const HTTP_METHOD_GET = 'GET';
export const HTTP_METHOD_POST = 'POST';
export const HTTP_METHOD_PUT = 'PUT';
export const HTTP_METHOD_DELETE = 'DELETE';
export const INTERVAL_TYPE_MINUTES = 'minute';
export const INTERVAL_TYPE_HOURS = 'hour';
export const INTERVAL_TYPE_DAYS = 'day';
export const RESPONSE_FORMAT_JSON = 'JSON';
export const RESPONSE_FORMAT_XML = 'XML';
export const RESPONSE_FORMAT_TEXT = 'TEXT';
export const CONDITION_TYPE_FIELD_PATH = 'field_path';
export const CONDITION_TYPE_JSONPATH = 'jsonpath';
export const CONDITION_TYPE_REGEX = 'regex';
export const CONDITION_TYPE_TEXT_SEARCH = 'text_search';
export const OPERATOR_EQUALS = 'equals';
export const OPERATOR_NOT_EQUALS = 'not_equals';
export const OPERATOR_CONTAINS = 'contains';
export const OPERATOR_NOT_CONTAINS = 'not_contains';
export const OPERATOR_GREATER_THAN = 'greater_than';
export const OPERATOR_LESS_THAN = 'less_than';
export const OPERATOR_GREATER_THAN_OR_EQUALS = 'greater_than_or_equals';
export const OPERATOR_LESS_THAN_OR_EQUALS = 'less_than_or_equals';
export const OPERATOR_IS_EMPTY = 'is_empty';
export const OPERATOR_IS_NOT_EMPTY = 'is_not_empty';
export const OPERATOR_STARTS_WITH = 'starts_with';
export const OPERATOR_ENDS_WITH = 'ends_with';
export const OPERATOR_MATCHES_REGEX = 'matches_regex';
export const OPERATOR_EXISTS = 'exists';
export const OPERATOR_NOT_EXISTS = 'not_exists';
export const OPERATOR_IN_LIST = 'in_list';
export const OPERATOR_NOT_IN_LIST = 'not_in_list';
export const TIMEOUT_ERROR = 'error';
export const TIMEOUT_PATH = 'timeout_path';

export const CONDITION_TYPES = [
  { value: CONDITION_TYPE_FIELD_PATH, label: 'Field Path', description: 'Simple dot notation (e.g., data.status)' },
  { value: CONDITION_TYPE_JSONPATH, label: 'JSONPath', description: 'JSONPath expression (e.g., $.data.items[*].status)' },
  { value: CONDITION_TYPE_REGEX, label: 'Regex', description: 'Match regex pattern against entire response' },
  { value: CONDITION_TYPE_TEXT_SEARCH, label: 'Text Search', description: 'Search for text with AND/OR logic (e.g., contains X AND Y OR Z)' },
];

export const CONDITION_OPERATORS = [
  { value: OPERATOR_EQUALS, label: 'Equals', needsValue: true },
  { value: OPERATOR_NOT_EQUALS, label: 'Not equals', needsValue: true },
  { value: OPERATOR_CONTAINS, label: 'Contains', needsValue: true },
  { value: OPERATOR_NOT_CONTAINS, label: 'Does not contain', needsValue: true },
  { value: OPERATOR_STARTS_WITH, label: 'Starts with', needsValue: true },
  { value: OPERATOR_ENDS_WITH, label: 'Ends with', needsValue: true },
  { value: OPERATOR_GREATER_THAN, label: 'Greater than', needsValue: true },
  { value: OPERATOR_LESS_THAN, label: 'Less than', needsValue: true },
  { value: OPERATOR_GREATER_THAN_OR_EQUALS, label: 'Greater than or equals', needsValue: true },
  { value: OPERATOR_LESS_THAN_OR_EQUALS, label: 'Less than or equals', needsValue: true },
  { value: OPERATOR_MATCHES_REGEX, label: 'Matches regex', needsValue: true },
  { value: OPERATOR_IN_LIST, label: 'In list (comma-separated)', needsValue: true },
  { value: OPERATOR_NOT_IN_LIST, label: 'Not in list (comma-separated)', needsValue: true },
  { value: OPERATOR_IS_EMPTY, label: 'Is empty', needsValue: false },
  { value: OPERATOR_IS_NOT_EMPTY, label: 'Is not empty', needsValue: false },
  { value: OPERATOR_EXISTS, label: 'Exists', needsValue: false },
  { value: OPERATOR_NOT_EXISTS, label: 'Does not exist', needsValue: false },
];

const keyAuto = () => 'await_event_' + (Math.floor(Math.random() * 90000000) + 10000).toString();

const initData = () => ({
  eventSourceMode: MODE_WEBHOOK,
  webhookMode: WEBHOOK_MODE_STATIC,
  webhookToken: '',
  webhookCorrelationField: '',
  pollingEndpointUrl: '',
  pollingHttpMethod: HTTP_METHOD_GET,
  pollingHeaders: [],
  pollingQueryParams: [],
  pollingRequestBody: '',
  pollingResponseFormat: RESPONSE_FORMAT_JSON,
  pollingInterval: { value: 5, type: INTERVAL_TYPE_MINUTES },
  condition: {
    type: CONDITION_TYPE_FIELD_PATH,
    expression: '',
    fieldPath: '',
    operator: OPERATOR_EQUALS,
    expectedValue: '',
    textSearchRules: [],
  },
  timeoutEnabled: false,
  timeout: { value: 1, type: INTERVAL_TYPE_HOURS, behavior: TIMEOUT_ERROR },
  outputKey: keyAuto(),
});

const onLoadData = (rawData) => {
  const d = JSON.parse(JSON.stringify(rawData));
  if (!d.eventSourceMode) d.eventSourceMode = MODE_WEBHOOK;
  if (!d.webhookMode) d.webhookMode = WEBHOOK_MODE_STATIC;
  if (!d.pollingHttpMethod) d.pollingHttpMethod = HTTP_METHOD_GET;
  if (!d.pollingResponseFormat) d.pollingResponseFormat = RESPONSE_FORMAT_JSON;
  if (!d.pollingHeaders) d.pollingHeaders = [];
  if (!d.pollingQueryParams) d.pollingQueryParams = [];
  if (!d.pollingInterval) d.pollingInterval = { value: 5, type: INTERVAL_TYPE_MINUTES };
  if (!d.condition) {
    d.condition = {
      type: CONDITION_TYPE_FIELD_PATH,
      expression: '',
      operator: OPERATOR_EQUALS,
      expectedValue: '',
      textSearchRules: [],
    };
  }
  if (!d.condition.textSearchRules) d.condition.textSearchRules = [];
  if (d.condition.fieldPath && !d.condition.expression) d.condition.expression = d.condition.fieldPath;
  if (!d.condition.type) d.condition.type = CONDITION_TYPE_FIELD_PATH;
  if (d.timeoutEnabled === undefined) d.timeoutEnabled = false;
  if (!d.timeout) d.timeout = { value: 1, type: INTERVAL_TYPE_HOURS, behavior: TIMEOUT_ERROR };
  // v1 compat: migrate outputPlaceholderKey → outputKey
  if (d.outputPlaceholderKey && !rawData.outputKey) {
    d.outputKey = d.outputPlaceholderKey;
  }
  delete d.outputPlaceholderKey;
  return d;
};

const { createStore, getStore, defaultStore } = createComponentStore({
  type: 'await_external_event',
  endpoint: 'await_external_event',
  registryKey: 'await_external_event',
  initData,
  onLoadData,
  extraActions: (set, get) => ({
    // Override setTemplateData with deep clone
    setTemplateData: (data) => set({ data: JSON.parse(JSON.stringify(data)) }),

    setEventSourceMode: (mode) => {
      set({ data: { ...get().data, eventSourceMode: mode } });
    },

    setWebhookMode: (mode) => {
      set({ data: { ...get().data, webhookMode: mode } });
    },

    setWebhookToken: (token) => {
      set({ data: { ...get().data, webhookToken: token } });
    },

    setWebhookCorrelationField: (field) => {
      set({ data: { ...get().data, webhookCorrelationField: field } });
    },

    setWebhookCorrelationValue: (value) => {
      set({ data: { ...get().data, webhookCorrelationValue: value } });
    },

    setPollingEndpointUrl: (url) => {
      set({ data: { ...get().data, pollingEndpointUrl: url } });
    },

    setPollingHttpMethod: (method) => {
      const updates = { pollingHttpMethod: method };
      if (method === HTTP_METHOD_GET || method === HTTP_METHOD_DELETE) {
        updates.pollingRequestBody = '';
      }
      set({ data: { ...get().data, ...updates } });
    },

    setPollingRequestBody: (body) => {
      set({ data: { ...get().data, pollingRequestBody: body } });
    },

    setPollingResponseFormat: (format) => {
      set({ data: { ...get().data, pollingResponseFormat: format } });
    },

    addPollingHeader: () => {
      const headers = [...get().data.pollingHeaders, { key: '', value: '' }];
      set({ data: { ...get().data, pollingHeaders: headers } });
    },

    updatePollingHeader: (index, field, value) => {
      const headers = [...get().data.pollingHeaders];
      if (headers[index]) {
        headers[index] = { ...headers[index], [field]: value };
        set({ data: { ...get().data, pollingHeaders: headers } });
      }
    },

    removePollingHeader: (index) => {
      const headers = [...get().data.pollingHeaders];
      headers.splice(index, 1);
      set({ data: { ...get().data, pollingHeaders: headers } });
    },

    setPollingHeaders: (headers) => {
      set({ data: { ...get().data, pollingHeaders: [...headers] } });
    },

    addPollingQueryParam: () => {
      const params = [...get().data.pollingQueryParams, { key: '', value: '' }];
      set({ data: { ...get().data, pollingQueryParams: params } });
    },

    updatePollingQueryParam: (index, field, value) => {
      const params = [...get().data.pollingQueryParams];
      if (params[index]) {
        params[index] = { ...params[index], [field]: value };
        set({ data: { ...get().data, pollingQueryParams: params } });
      }
    },

    removePollingQueryParam: (index) => {
      const params = [...get().data.pollingQueryParams];
      params.splice(index, 1);
      set({ data: { ...get().data, pollingQueryParams: params } });
    },

    setPollingQueryParams: (params) => {
      set({ data: { ...get().data, pollingQueryParams: [...params] } });
    },

    setPollingIntervalValue: (value) => {
      set({ data: { ...get().data, pollingInterval: { ...get().data.pollingInterval, value } } });
    },

    setPollingIntervalType: (type) => {
      set({ data: { ...get().data, pollingInterval: { ...get().data.pollingInterval, type } } });
    },

    setConditionType: (type) => {
      set({ data: { ...get().data, condition: { ...get().data.condition, type } } });
    },

    setConditionExpression: (value) => {
      set({
        data: {
          ...get().data,
          condition: { ...get().data.condition, expression: value, fieldPath: value },
        },
      });
    },

    setConditionFieldPath: (value) => {
      set({
        data: {
          ...get().data,
          condition: { ...get().data.condition, fieldPath: value, expression: value },
        },
      });
    },

    setConditionOperator: (value) => {
      set({ data: { ...get().data, condition: { ...get().data.condition, operator: value } } });
    },

    setConditionExpectedValue: (value) => {
      set({ data: { ...get().data, condition: { ...get().data.condition, expectedValue: value } } });
    },

    setCondition: (condition) => {
      set({ data: { ...get().data, condition: { ...condition } } });
    },

    addTextSearchRule: () => {
      const rules = [...(get().data.condition.textSearchRules || [])];
      rules.push({ term: '', contains: true, group: 0, caseSensitive: false });
      set({
        data: {
          ...get().data,
          condition: { ...get().data.condition, textSearchRules: rules },
        },
      });
    },

    updateTextSearchRule: (index, field, value) => {
      const rules = [...(get().data.condition.textSearchRules || [])];
      if (rules[index]) {
        rules[index] = { ...rules[index], [field]: value };
        set({
          data: {
            ...get().data,
            condition: { ...get().data.condition, textSearchRules: rules },
          },
        });
      }
    },

    removeTextSearchRule: (index) => {
      const rules = [...(get().data.condition.textSearchRules || [])];
      rules.splice(index, 1);
      set({
        data: {
          ...get().data,
          condition: { ...get().data.condition, textSearchRules: rules },
        },
      });
    },

    setTextSearchRules: (rules) => {
      set({
        data: {
          ...get().data,
          condition: { ...get().data.condition, textSearchRules: [...rules] },
        },
      });
    },

    setTimeoutEnabled: (enabled) => {
      set({ data: { ...get().data, timeoutEnabled: enabled } });
    },

    setTimeoutValue: (value) => {
      set({ data: { ...get().data, timeout: { ...get().data.timeout, value } } });
    },

    setTimeoutType: (type) => {
      set({ data: { ...get().data, timeout: { ...get().data.timeout, type } } });
    },

    setTimeoutBehavior: (behavior) => {
      set({ data: { ...get().data, timeout: { ...get().data.timeout, behavior } } });
    },

    setTimeout: (timeout) => {
      set({ data: { ...get().data, timeout: { ...timeout } } });
    },

    setOutputPlaceholderKey: (value) => {
      set({ data: { ...get().data, outputKey: value } });
    },
  }),
});

export { getStore as getAwaitExternalEventStore };

export default defaultStore;
